import type { Assessment, Student } from "@/types/assessment";
import { apiDelete, apiGet, apiPost, apiPut } from "@/services/api/client";

interface StudentsResponse {
  items: Student[];
  page: number;
  pageSize: number;
  total: number;
}

export async function fetchStudents(query = ""): Promise<Student[]> {
  const items: Student[] = [];
  let page = 1;
  let total = 0;
  do {
    const response = await apiGet<StudentsResponse>(
      `/students?page=${page}&pageSize=100${query ? `&q=${encodeURIComponent(query)}` : ""}`,
    );
    items.push(...response.items);
    total = response.total;
    page += 1;
  } while (items.length < total);
  return items;
}

export async function fetchStudentById(studentId: string): Promise<Student | undefined> {
  return apiGet<Student>(`/students/${encodeURIComponent(studentId)}`);
}

export async function fetchAssessmentsByStudent(studentId: string): Promise<Assessment[]> {
  return apiGet<Assessment[]>(`/students/${encodeURIComponent(studentId)}/assessments`);
}

export interface StudentPayload {
  name: string;
  gender: Student["profile"]["gender"];
  birthdate: string;
  school: string;
  classroom: string;
  group?: string;
  enrollmentDate?: string;
  disabilityCategory?: string;
  disabilityCause?: string;
  disabilityLevel?: string;
  guardians: GuardianPayload[];
  notes?: string;
}

export interface GuardianPayload {
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
  address?: string;
  occupation?: string;
}

export async function createStudent(payload: StudentPayload): Promise<Student> {
  return apiPost<Student>("/students", payload);
}

export async function updateStudent(studentId: string, payload: StudentPayload): Promise<Student> {
  return apiPut<Student>(`/students/${encodeURIComponent(studentId)}`, payload);
}

export async function deleteStudent(studentId: string): Promise<void> {
  await apiDelete(`/students/${encodeURIComponent(studentId)}`);
}
